import * as classNames from 'classnames'
import * as React from 'react'
import { Route, RouteComponentProps, Switch } from 'react-router'
import {
  IBankAccountObj,
  ICreateBankAccountObj,
  ILegalEntityObj,
  IMap,
  IMessage,
  IOrgObj,
  IStateMetadata,
  ICreateOrgSubscriptionDTO,
  IUpdateOrgSubscriptionDTO
} from '../../../types'
import UIMessages from '../../../utils/ui-messages'
import BankAccountPane from '../create/bankaccount/BankAccount'
import LegalEntityPane from '../create/legalentity/LegalEntity'
import OrganizationInfoPane from '../create/organizationinfo/OrganizationInfo'
import OrganizationLocationPane from '../location/OrganizationLocation'
import OrganizationSubscriptionPane from '../create/organizationSubscriptionPane/OrganizationSubscriptionPane'
import CreateOrganizationStepSelector from '../create/Selector/CreateOrganizationStepSelector'

interface ICreateOrganizationPaneProps {
  bankAccounts: IMap<IBankAccountObj>
  location: any
  match: any
  metadata: IStateMetadata
  orgId: string
  legalEntity: ILegalEntityObj
  organization: IOrgObj
  routeProps: RouteComponentProps
  fetchOrg: (orgId: string) => Promise<any>
  getOrganizationLegalEntity: (orgId: string) => Promise<any>
  createOrg: (data: IOrgObj) => Promise<any>
  updateOrg: (data: IOrgObj) => Promise<any>
  createOrgClear: () => Promise<any>
  createOrganizationBankAccount: (data: ICreateBankAccountObj, orgId: string) => Promise<any>
  updateOrganizationLegalEntity: (data: ILegalEntityObj, orgId: string) => Promise<any>
  createOrganizationLegalEntity: (data: ILegalEntityObj, orgId: string) => Promise<any>
  removeOrganizationBankAccount: (orgId: string, bankAccountId: string) => Promise<any>
  setOrganizationDefaultBankAccount: (orgId: string, bankAccountId: string) => Promise<any>
  createOrgSubscription: (dto: ICreateOrgSubscriptionDTO) => Promise<any>
  updateOrgSubscription: (dto: IUpdateOrgSubscriptionDTO) => Promise<any>
  resetOrgMetadata: () => void
}

interface ICreateOrganizationPaneState {
  bankAccounts: IMap<IBankAccountObj>
  bankAccount: ICreateBankAccountObj
  currentError: IMessage
  currentStep: number
  isSaved: boolean
  isSaving: boolean
  legalEntity: ILegalEntityObj
  organization: IOrgObj
  steps: boolean[]
}

class EditOrganizationPane extends React.Component<ICreateOrganizationPaneProps, ICreateOrganizationPaneState> {
  constructor(props: ICreateOrganizationPaneProps) {
    super(props)
    this.state = {
      bankAccounts: {},
      bankAccount: {
        accountHolderName: '',
        accountHolderType: '',
        accountNumber: '',
        country: '',
        currency: '',
        isPrimary: false,
        routingNumber: '',
        status: ''
      },
      currentError: { isError: false, message: '' },
      currentStep: 1,
      isSaved: true,
      isSaving: false,
      legalEntity: {
        addressLine1: '',
        addressLine2: '',
        businessName: '',
        businessTaxId: 0,
        city: '',
        firstName: '',
        lastName: '',
        postalCode: '',
        state: '',
        type: ''
      },
      organization: Object.assign({}, this.props.organization),
      steps: [true, true, true, true, true]
    }
  }

  public componentDidMount() {
    this.setState({ isSaving: true }, () => {
      this.props.fetchOrg(this.props.orgId)
      this.props.getOrganizationLegalEntity(this.props.orgId).then(() => {
        const le: any = this.props.legalEntity as any
        this.setState({
          isSaving: false,
          legalEntity: le ? Object.assign({}, le) : {},
          bankAccounts: le ? Object.assign([], le.bankAccounts) : []
        })
      })
    })
  }

  public componentDidUpdate(prevProps: ICreateOrganizationPaneProps) {
    if (prevProps.metadata !== this.props.metadata) {
      const { processingError, processingSuccess } = this.props.metadata
      if (processingError) {
        this.setState({ isSaving: false })
        this.showMessage(processingError, true)
        return
      }
      if (processingSuccess) {
        this.setState({
          bankAccount: {
            accountHolderName: '',
            accountHolderType: '',
            accountNumber: '',
            country: '',
            currency: '',
            isPrimary: false,
            routingNumber: '',
            status: ''
          },
          isSaved: true,
          isSaving: false
        })
        this.showMessage(processingSuccess, false)
      }
    }
    if (prevProps.legalEntity !== this.props.legalEntity) {
      const le: any = this.props.legalEntity as any
      this.setState({ bankAccounts: le ? Object.assign([], le.bankAccounts) : [] })
    }
    if (this.props.location !== prevProps.location) {
      this.onRouteChange()
    }
    if (this.props.organization !== prevProps.organization) {
      this.setState({
        organization: this.props.organization
      })
    }
  }

  public componentWillUnmount() {
    this.props.createOrgClear()
    this.props.resetOrgMetadata()
  }

  public setOrganizationPlan = (planId: string, stripeSourceId: string): Promise<any> => {
    const { organization } = this.state
    if (organization.subscription) {
      return this.props.updateOrgSubscription({
        id: organization.subscription.id,
        planId,
        stripeSourceId
      })
    } else {
      return this.props.createOrgSubscription({
        orgId: organization.id,
        planId,
        stripeSourceId,
        email: organization.email,
        name: organization.name
      })
    }
  }

  public onRemoveBankAccount = (bankAccountId: string): Promise<any> => {
    this.setState({ isSaving: true })
    return this.props.removeOrganizationBankAccount(this.props.orgId, bankAccountId)
  }

  public onSetPrimaryBankAccount = (bankAccountId: string): Promise<any> => {
    this.setState({ isSaving: true })
    return this.props
      .setOrganizationDefaultBankAccount(this.props.orgId, bankAccountId)
      .then(() =>
        this.setState({
          currentError: { isError: false, message: UIMessages.organization.bankAccount.successfullySetDefault }
        })
      )
      .catch(() =>
        this.setState({ currentError: { isError: true, message: UIMessages.organization.bankAccount.errorOccurred } })
      )
  }

  public onSaveBankAccount = (bankAccount: ICreateBankAccountObj): Promise<any> => {
    this.setState({ bankAccount, isSaving: true })
    return this.props.createOrganizationBankAccount(bankAccount, this.props.orgId)
  }

  public onSaveLegalEntity = (legalEntity: ILegalEntityObj): Promise<any> => {
    const original = Object.assign({}, this.props.legalEntity)
    this.setState({ legalEntity, isSaving: false })
    return this.props.updateOrganizationLegalEntity({ ...original, ...legalEntity }, this.props.orgId)
  }

  public onSaveOrganizationInfo = (organization: IOrgObj): Promise<any> => {
    this.setState({ organization, isSaving: false })
    return this.props.createOrg(organization)
  }

  public onSaveOrganizationLocation = (organization: IOrgObj): Promise<any> => {
    this.setState({ organization, isSaving: true })
    return this.props.updateOrg(organization)
  }

  public render() {
    const { bankAccounts } = this.state
    const { bankAccount, currentError, currentStep, isSaved, isSaving, legalEntity, organization } = this.state
    const { path: matchPath } = this.props.match

    const getOrganizationInfo = () => {
      if (!organization || !organization.id) {
        return null
      }

      return (
        <OrganizationInfoPane
          currentStep={1}
          isSaved={isSaved}
          isSaving={isSaving}
          orgId={this.props.orgId}
          isCompleted={true}
          isEdit={true}
          organization={organization}
          onCompleteStep={this.onCompleteStep}
          onSaveOrganizationInfo={this.onSaveOrganizationInfo}
          showMessage={this.showMessage}
        />
      )
    }

    const getSubscriptionPlanPane = () => {
      return (
        <OrganizationSubscriptionPane
          currentStep={2}
          orgId={this.props.orgId}
          isSaving={isSaving}
          isEdit={true}
          organization={organization}
          isCompleted={this.state.steps[0]}
          onCompleteStep={this.onCompleteStep}
          onPlanSelect={this.setOrganizationPlan}
        />
      )
    }

    const getOrganizationLocation = () => {
      return (
        <OrganizationLocationPane
          currentStep={3}
          isSaved={isSaved}
          isSaving={isSaving}
          isEdit={true}
          orgId={this.props.orgId}
          isCompleted={this.state.steps[1]}
          organization={organization}
          metadata={this.props.metadata}
          onCompleteStep={this.onCompleteStep}
          onSaveOrganizationLocation={this.onSaveOrganizationLocation}
          showMessage={this.showMessage}
        />
      )
    }

    const getLegalEntity = () => {
      return (
        <LegalEntityPane
          currentStep={4}
          isSaved={isSaved}
          isSaving={isSaving}
          isEdit={true}
          orgId={this.props.orgId}
          organization={organization}
          isCompleted={true}
          legalEntity={legalEntity}
          onCompleteStep={this.onCompleteStep}
          onSaveLegalEntity={this.onSaveLegalEntity}
          showMessage={this.showMessage}
        />
      )
    }

    const getBankAccount = () => {
      return (
        <BankAccountPane
          bankAccount={bankAccount}
          isCompleted={true}
          bankAccounts={bankAccounts}
          organization={organization}
          currentStep={5}
          isEdit={true}
          orgId={this.props.orgId}
          history={this.props.routeProps.history}
          isSaved={isSaved}
          isSaving={isSaving}
          match={this.props.match}
          onCompleteStep={this.onCompleteStep}
          onRemoveBankAccount={this.onRemoveBankAccount}
          onSaveBankAccount={this.onSaveBankAccount}
          onSetPrimaryBankAccount={this.onSetPrimaryBankAccount}
          showMessage={this.showMessage}
        />
      )
    }
    return (
      <div className='col-12'>
        {currentError.message.length > 0 && (
          <div
            className={classNames(
              'ErrorMsgDiv pr1 pl3 pt1 pb1 font-white',
              { 'bg-red': currentError.isError },
              { 'bg-green': !currentError.isError }
            )}
          >
            {currentError.message}
          </div>
        )}
        <div className='p2'>
          <span className='font-blue-grey fs-20'>{UIMessages.common.editOrganization}</span>
          &nbsp;&nbsp;&nbsp;
          <span className='font-small font-light-grey'>Step {currentStep} of 5</span>
        </div>
        <CreateOrganizationStepSelector
          isSaved={isSaved}
          isStepComplete={this.isStepComplete}
          path={matchPath}
          pathname={location.pathname}
          orgId={organization.id}
        />
        <Switch>
          <Route path={`${matchPath}/organization-info`} render={getOrganizationInfo} key='org-info' />
          <Route
            path={`${matchPath}/organization-subscription`}
            render={getSubscriptionPlanPane}
            key='org-subscription'
          />
          <Route path={`${matchPath}/organization-location`} render={getOrganizationLocation} key='org-location' />
          <Route path={`${matchPath}/legal-entity`} render={getLegalEntity} key='org-entity' />
          <Route path={`${matchPath}/bank-account`} render={getBankAccount} key='org-account' />
        </Switch>
      </div>
    )
  }

  private isStepComplete = (step: number) => {
    return step < 1 || step > 5 ? false : this.state.steps[step - 1]
  }

  private onCompleteStep = (step: number) => {
    if (step < 1 || step > 5) {
      return
    }
    const steps = Object.assign([] as boolean[], this.state.steps)
    steps[step - 1] = true
    this.setState({ steps })
  }

  private onRouteChange = () => {
    if (/\/organization-info/.test(location.pathname) && this.state.currentStep !== 1) {
      this.setState({
        isSaved: false,
        isSaving: false,
        currentStep: 1,
        currentError: { message: '', isError: false }
      })
    } else if (/\/organization-subscription/.test(location.pathname) && this.state.currentStep !== 2) {
      this.setState({
        isSaved: false,
        isSaving: false,
        currentStep: 2,
        currentError: { message: '', isError: false }
      })
    } else if (/\/organization-location/.test(location.pathname) && this.state.currentStep !== 3) {
      this.setState({
        isSaved: false,
        isSaving: false,
        currentStep: 3,
        currentError: { message: '', isError: false }
      })
    } else if (/\/legal-entity/.test(location.pathname) && this.state.currentStep !== 4) {
      this.setState({
        isSaved: false,
        isSaving: false,
        currentStep: 4,
        currentError: { message: '', isError: false }
      })
    } else if (/\/bank-account/.test(location.pathname) && this.state.currentStep !== 5) {
      this.setState({
        isSaved: false,
        isSaving: false,
        currentStep: 5,
        currentError: { message: '', isError: false }
      })
    }
  }

  private showMessage = (message: string, isError: boolean) => {
    if (this.state.currentError.message === message) {
      return
    }
    this.setState({ currentError: { message, isError } })
  }
}

export default EditOrganizationPane



// WEBPACK FOOTER //
// ./src/components/organizations/edit/EditOrganizationPane.tsx